export type EAdminAvatarView = "manage" | "review"
export type EAvatarReviewGroup = "none" | "vibe" | "gender"
export type EAvatarStatusFilter = "all" | "active" | "inactive"
export type EAvatarMediaFilter = "all" | "image" | "video"
export type EAvatarQaQueueFilter =
  | "awaiting_review"
  | "image_review"
  | "video_review"
  | "video_needed"
  | "failed"
  | "approved"
  | "all"
export type EAvatarCandidatesFilter = "any" | "ranked" | "unranked"
export type EAvatarVoiceFilter = "any" | "assigned" | "unassigned"
export type EAvatarAutoQaFilter = "any" | "pending" | "fail" | "warn" | "unscored"
export type EAvatarVideoQaFilter = "any" | "unreviewed" | "fail" | "pass" | "no_video"

export interface IAdminAvatarsSearch {
  id?: string
  view?: EAdminAvatarView
  q?: string
  status?: EAvatarStatusFilter
  media?: EAvatarMediaFilter
  group?: EAvatarReviewGroup
  queue?: EAvatarQaQueueFilter
  kind?: string
  gender?: string
  age?: string
  ethnicity?: string
  vibe?: string
  candidates?: EAvatarCandidatesFilter
  voice?: EAvatarVoiceFilter
  autoQa?: EAvatarAutoQaFilter
  videoQa?: EAvatarVideoQaFilter
}

const VIEW_VALUES = new Set<EAdminAvatarView>(["manage", "review"])
const GROUP_VALUES = new Set<EAvatarReviewGroup>(["none", "vibe", "gender"])
const STATUS_VALUES = new Set<EAvatarStatusFilter>(["all", "active", "inactive"])
const MEDIA_VALUES = new Set<EAvatarMediaFilter>(["all", "image", "video"])
const QUEUE_VALUES = new Set<EAvatarQaQueueFilter>([
  "awaiting_review",
  "image_review",
  "video_review",
  "video_needed",
  "failed",
  "approved",
  "all",
])
const CANDIDATE_VALUES = new Set<EAvatarCandidatesFilter>(["any", "ranked", "unranked"])
const VOICE_VALUES = new Set<EAvatarVoiceFilter>(["any", "assigned", "unassigned"])
const AUTO_QA_VALUES = new Set<EAvatarAutoQaFilter>(["any", "pending", "fail", "warn", "unscored"])
const VIDEO_QA_VALUES = new Set<EAvatarVideoQaFilter>(["any", "unreviewed", "fail", "pass", "no_video"])

const asString = (value: unknown, maxLength = 100): string | undefined => {
  if (typeof value !== "string") return undefined
  const trimmed = value.trim()
  if (!trimmed) return undefined
  return trimmed.slice(0, maxLength)
}

const asEnum = <T extends string>(value: unknown, values: ReadonlySet<T>): T | undefined => {
  return typeof value === "string" && values.has(value as T) ? (value as T) : undefined
}

export const validateAdminAvatarsSearch = (raw: Record<string, unknown>): IAdminAvatarsSearch => {
  const search: IAdminAvatarsSearch = {}
  const id = asString(raw.id, 191)
  const q = asString(raw.q)
  const view = asEnum(raw.view, VIEW_VALUES)
  const status = asEnum(raw.status, STATUS_VALUES)
  const media = asEnum(raw.media, MEDIA_VALUES)
  const group = asEnum(raw.group, GROUP_VALUES)
  const queue = asEnum(raw.queue, QUEUE_VALUES)
  const candidates = asEnum(raw.candidates, CANDIDATE_VALUES)
  const voice = asEnum(raw.voice, VOICE_VALUES)
  const autoQa = asEnum(raw.autoQa, AUTO_QA_VALUES)
  const videoQa = asEnum(raw.videoQa, VIDEO_QA_VALUES)

  if (id) search.id = id
  if (q) search.q = q
  if (view && view !== "review") search.view = view
  if (status && status !== "all") search.status = status
  if (media && media !== "all") search.media = media
  if (group && group !== "none") search.group = group
  if (queue && queue !== "awaiting_review") search.queue = queue
  if (candidates && candidates !== "any") search.candidates = candidates
  if (voice && voice !== "any") search.voice = voice
  if (autoQa && autoQa !== "any") search.autoQa = autoQa
  if (videoQa && videoQa !== "any") search.videoQa = videoQa

  const passthroughKeys = ["kind", "gender", "age", "ethnicity", "vibe"] as const
  for (const key of passthroughKeys) {
    const value = asString(raw[key])
    if (value) search[key] = value
  }
  return search
}
